import { useState } from "react";
import { useHousehold } from "@/hooks/useHousehold";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Shield, X } from "lucide-react";
import { toast } from "sonner";

interface Props {
  onClose: () => void;
}

export default function CancellationModal({ onClose }: Props) {
  const { cancelSubscription } = useHousehold();
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [accessUntil, setAccessUntil] = useState<string | null>(null);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const result = await cancelSubscription();
      if (result?.access_until) {
        setAccessUntil(new Date(result.access_until).toLocaleDateString());
      }
      setCancelled(true);
      toast.success("Subscription cancelled successfully.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to cancel subscription.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/90 backdrop-blur-md" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label="Cancel subscription" className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl glass-card glow-border p-6 sm:p-8">
        <button type="button" aria-label="Close" onClick={onClose} className="absolute right-4 top-4 text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>

        {cancelled ? (
          <div className="space-y-4 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-secondary/30 bg-secondary/10">
              <AlertTriangle className="h-7 w-7 text-secondary" />
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground">Subscription Cancelled</h2>
            <p className="text-sm text-muted-foreground">
              Your household keeps access until <span className="font-semibold text-foreground">{accessUntil || "the end of the billing period"}</span>.
            </p>
            <Button onClick={onClose} className="w-full">Close</Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-destructive/30 bg-destructive/10">
                <Shield className="h-7 w-7 text-destructive" />
              </div>
              <h2 className="mb-3 font-heading text-xl font-bold text-foreground">Cancel Subscription?</h2>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Canceling pauses household access after the current billing period ends, including lessons, modules, simulations, and parent tracking tools.
              </p>
            </div>

            <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-4">
              <p className="text-xs font-semibold text-foreground">What changes after cancellation:</p>
              <ul className="space-y-1 text-xs text-muted-foreground">
                <li>• Current access continues until the billing period ends</li>
                <li>• Billing stops after the period closes</li>
                <li>• You can reactivate later from Billing</li>
              </ul>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button onClick={onClose} className="flex-1">Keep Subscription</Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={cancelling}
                className="flex-1 border-destructive/50 text-destructive hover:bg-destructive/10"
              >
                {cancelling ? "Cancelling..." : "Continue Cancellation"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
